import type { Workout } from './types';
import type { Category } from '../scoring/types';

/**
 * One lift, reduced to what the log form needs to show it back.
 *
 * Deliberately not a `Workout`: the form folds in a lift the moment it is
 * submitted, before any document exists to read back, and inventing the other
 * twenty fields to do that would be a lie.
 */
export interface LiftBest {
  workoutDate: string;
  monthKey: string;
  enteredWeightLb: number;
  reps: number;
  estimated1RmLb: number;
  /**
   * What it scored at the time, with the body weight and sex of the day. A
   * historical fact, so it is carried rather than recomputed - the what-if
   * screen can change those inputs, and doing so must not rewrite the past.
   */
  points: number;
  inputWeightMultiplier?: number;
  bodyWeightFactor?: number;
}

export interface LiftHighlights {
  /** Best ever for this exercise. */
  pr?: LiftBest;
  /** Best in the current month for this exercise. */
  monthBest?: LiftBest;
}

/** Exercise id to that exercise's PR and month best. */
export type LiftHighlightMap = Record<string, LiftHighlights>;

/**
 * Which of two lifts is the better one.
 *
 * Ranked on estimated 1RM, so heavier-for-fewer and lighter-for-more compare on
 * the same footing. Ties go to the earlier date: a PR belongs to the day it was
 * first hit, not the last time it was matched.
 */
function beats(candidate: LiftBest, incumbent: LiftBest | undefined): boolean {
  if (!incumbent) return true;
  if (candidate.estimated1RmLb !== incumbent.estimated1RmLb) {
    return candidate.estimated1RmLb > incumbent.estimated1RmLb;
  }
  return candidate.workoutDate < incumbent.workoutDate;
}

function toBest(workout: Workout): LiftBest {
  return {
    workoutDate: workout.workoutDate,
    monthKey: workout.monthKey,
    enteredWeightLb: workout.enteredWeightLb,
    reps: workout.reps,
    estimated1RmLb: workout.estimated1RmLb,
    points: workout.points,
    inputWeightMultiplier: workout.inputWeightMultiplier,
    bodyWeightFactor: workout.bodyWeightFactor,
  };
}

/**
 * Builds each exercise's PR and current-month best from a person's whole history.
 *
 * @param workouts Every workout for one person, in any order.
 * @param monthKey The month that counts as "this month", e.g. "2026-08".
 */
export function buildLiftHighlights(workouts: Workout[], monthKey: string): LiftHighlightMap {
  const map: LiftHighlightMap = {};
  for (const workout of workouts) {
    const best = toBest(workout);
    const entry = (map[workout.exerciseId] ??= {});
    if (beats(best, entry.pr)) entry.pr = best;
    if (workout.monthKey === monthKey && beats(best, entry.monthBest)) entry.monthBest = best;
  }
  return map;
}

/**
 * Folds a just-logged lift into the map, so the form reflects it immediately
 * rather than after a refetch.
 *
 * @returns A new map; the argument is not modified.
 */
export function foldLiftIntoHighlights(
  map: LiftHighlightMap,
  exerciseId: string,
  lift: LiftBest,
  monthKey: string,
): LiftHighlightMap {
  const entry = map[exerciseId] ?? {};
  const next: LiftHighlights = { ...entry };
  if (beats(lift, entry.pr)) next.pr = lift;
  if (lift.monthKey === monthKey && beats(lift, entry.monthBest)) next.monthBest = lift;
  return { ...map, [exerciseId]: next };
}

/** A category's best points, all time and within the current month. */
export interface CategoryRecord {
  bestPoints?: number;
  bestMonthPoints?: number;
}

export type CategoryRecordMap = Partial<Record<Category, CategoryRecord>>;

/**
 * Each category's best points from a person's whole history.
 *
 * The counterpart to buildLiftHighlights: together they are everything
 * `awardBadges` needs to judge a lift the instant it is logged, off the history
 * the log page already reads.
 */
export function buildCategoryRecords(workouts: Workout[], monthKey: string): CategoryRecordMap {
  const map: CategoryRecordMap = {};
  for (const workout of workouts) {
    const entry = (map[workout.category] ??= {});
    if (entry.bestPoints === undefined || workout.points > entry.bestPoints) {
      entry.bestPoints = workout.points;
    }
    if (
      workout.monthKey === monthKey &&
      (entry.bestMonthPoints === undefined || workout.points > entry.bestMonthPoints)
    ) {
      entry.bestMonthPoints = workout.points;
    }
  }
  return map;
}

/** Folds a just-logged lift into the category records. Returns a new map. */
export function foldLiftIntoCategoryRecords(
  map: CategoryRecordMap,
  category: Category,
  points: number,
  liftMonthKey: string,
  monthKey: string,
): CategoryRecordMap {
  const entry = map[category] ?? {};
  const next: CategoryRecord = { ...entry };
  if (next.bestPoints === undefined || points > next.bestPoints) next.bestPoints = points;
  if (
    liftMonthKey === monthKey &&
    (next.bestMonthPoints === undefined || points > next.bestMonthPoints)
  ) {
    next.bestMonthPoints = points;
  }
  return { ...map, [category]: next };
}
